/**
 * Multi-day neighbor comparison analysis engine.
 *
 * Analyzes 10 days of quarter-hour production data for a main meter
 * and up to 5 neighbor meters. All comparisons use Wh/kWp normalization.
 *
 * Returns structured analysis with text segments that can reference
 * specific dates (for clickable day navigation in the UI).
 *
 * @module multiDayAnalysis
 */

/**
 * @typedef {Object} DayStats
 * @property {string} dateStr    - 'yyyy-MM-dd'
 * @property {number} total      - Total production (Wh/kWp)
 * @property {number} peak       - Peak quarter-hour value
 * @property {string} peakTime   - HH:mm of peak
 * @property {number} slots      - Number of productive quarter-hours
 * @property {number} morning    - Sum of production 06:00–12:00 (Wh/kWp)
 * @property {number} afternoon  - Sum of production 12:00–20:00 (Wh/kWp)
 * @property {boolean} hasData   - Whether any data exists for this day
 */

/**
 * @typedef {Object} AnalysisSegment
 * @property {string} text       - Text content
 * @property {string} [date]     - Optional date reference (yyyy-MM-dd) for clickable link
 * @property {string} [type]     - 'normal' | 'warning' | 'good' | 'critical'
 */

/**
 * @typedef {Object} MultiDayResult
 * @property {AnalysisSegment[]} segments  - Ordered text segments
 * @property {DayStats[]} dailyStats       - Per-day statistics for the main meter
 * @property {Object} summary              - Numeric summary values
 */

import { MONTHLY_SOLAR_FRACTIONS, MIN_DAYS_FOR_ANALYSIS } from './constants'
import { estimateOrientationAndTilt, daySmoothnessScore } from './solar'
import { formatDateNL } from './formatters'

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse quarter-hour data into per-day stats, normalized to Wh/kWp.
 */
function buildDailyStats(data, kwp, dateList) {
  const byDate = {}
  // Group raw data by date for smoothness scoring
  const rawByDate = {}
  if (data) data.forEach(item => {
    const d = item.date?.substring(0, 10)
    if (d) (rawByDate[d] = rawByDate[d] || []).push(item)
  })

  dateList.forEach(d => {
    byDate[d] = { dateStr: d, total: 0, peak: 0, peakTime: '', slots: 0, morning: 0, afternoon: 0, hasData: false, smoothness: 0 }
  })

  if (!data || data.length === 0) return Object.values(byDate)

  data.forEach(item => {
    const raw = Math.max(0, item.delta_power ?? 0)
    const val = kwp ? raw / kwp : raw
    const dateStr = item.date?.substring(0, 10) || ''
    const time = item.date?.substring(11, 16) || ''
    const hour = parseInt(time)

    if (!byDate[dateStr]) return // outside our date range

    const day = byDate[dateStr]
    day.hasData = true
    day.total += val
    if (val > day.peak) { day.peak = val; day.peakTime = time }
    if (raw > 0) day.slots++
    if (hour >= 6 && hour < 12) day.morning += val
    if (hour >= 12 && hour <= 20) day.afternoon += val
  })

  // Compute smoothness per day from raw (unnormalized) quarter-hour data
  Object.keys(byDate).forEach(d => {
    if (rawByDate[d] && rawByDate[d].length >= 20) {
      byDate[d].smoothness = Math.round(daySmoothnessScore(rawByDate[d]) * 100)
    }
  })

  return Object.values(byDate)
}

/**
 * Compute mean and standard deviation of an array.
 */
function meanStd(values) {
  if (values.length === 0) return { mean: 0, std: 0 }
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length
  return { mean, std: Math.sqrt(variance) }
}

/**
 * Compute linear regression slope (normalized per day).
 */
function trendSlope(values) {
  const n = values.length
  if (n < 3) return 0
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0
  for (let i = 0; i < n; i++) {
    sumX += i; sumY += values[i]; sumXY += i * values[i]; sumXX += i * i
  }
  return (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
}

// Solar functions imported from solar.js

// ─── Main analysis ──────────────────────────────────────────────────────────

/**
 * Analyze 10 days of quarter-hour data for main meter vs neighbors.
 *
 * @param {Object} params
 * @param {Array}  params.mainData         - Main meter quarter-hour data (10 days)
 * @param {Object} params.neighborDataMap  - { serialNumber: quarterHourData[] }
 * @param {Array}  params.neighbors        - [{ serialNumber, address }]
 * @param {number|null} params.mainWp      - Main meter total Wp
 * @param {Object} params.neighborWpMap    - { serialNumber: totalWp }
 * @param {string} params.matchType        - 'street' | 'postcode'
 * @param {string[]} params.dateList       - ['2026-03-21', '2026-03-22', ...]
 * @param {number|null} params.expectedProduction - Annual expected production (Wh)
 * @returns {MultiDayResult|null}
 */

export function analyzeMultiDay({
  mainData,
  neighborDataMap,
  neighbors,
  mainWp,
  neighborWpMap,
  matchType,
  dateList,
  expectedProduction,
}) {
  if (!mainData || mainData.length === 0 || !dateList || dateList.length === 0) return null

  const mainKwp = mainWp ? mainWp * 0.001 : null
  const label = matchType === 'postcode' ? 'vergelijkbare installaties' : 'buren'

  // ── Build per-day stats ──────────────────────────────────────────────────
  const mainDaily = buildDailyStats(mainData, mainKwp, dateList)
  const mainDaysWithData = mainDaily.filter(d => d.hasData)

  if (mainDaysWithData.length < MIN_DAYS_FOR_ANALYSIS) return null

  // Build neighbor daily stats
  const neighborDailyMap = {} // { serialNumber: DayStats[] }
  const activeNeighbors = []

  for (const neighbor of (neighbors || [])) {
    const nData = neighborDataMap?.[neighbor.serialNumber]
    if (!nData || nData.length === 0) continue

    const nWp = neighborWpMap?.[neighbor.serialNumber]
    const nKwp = (nWp ? nWp * 0.001 : null) || mainKwp

    const nDaily = buildDailyStats(nData, nKwp, dateList)
    const nDaysWithData = nDaily.filter(d => d.hasData)
    if (nDaysWithData.length < MIN_DAYS_FOR_ANALYSIS) continue

    neighborDailyMap[neighbor.serialNumber] = nDaily
    activeNeighbors.push(neighbor)
  }

  const hasNeighbors = activeNeighbors.length > 0

  // ── Compute per-day ratios (main / neighbor avg) ─────────────────────────
  const dailyRatios = [] // { dateStr, ratio, mainTotal, neighborAvg }

  for (const day of mainDaily) {
    if (!day.hasData) continue

    if (hasNeighbors) {
      let nSum = 0, nCount = 0
      for (const n of activeNeighbors) {
        const nDay = neighborDailyMap[n.serialNumber]?.find(d => d.dateStr === day.dateStr)
        if (nDay?.hasData && nDay.total > 0) { nSum += nDay.total; nCount++ }
      }
      const neighborAvg = nCount > 0 ? nSum / nCount : 0
      const ratio = neighborAvg > 0 ? (day.total / neighborAvg) * 100 : null
      dailyRatios.push({ dateStr: day.dateStr, ratio, mainTotal: day.total, neighborAvg })
    } else {
      dailyRatios.push({ dateStr: day.dateStr, ratio: null, mainTotal: day.total, neighborAvg: 0 })
    }
  }

  // ── Aggregate metrics ────────────────────────────────────────────────────
  const mainTotals = mainDaysWithData.map(d => d.total)
  const mainMornings = mainDaysWithData.map(d => d.morning)
  const mainAfternoons = mainDaysWithData.map(d => d.afternoon)
  const { mean: avgMainTotal, std: stdMainTotal } = meanStd(mainTotals)

  const ratioValues = dailyRatios.filter(r => r.ratio !== null).map(r => r.ratio)
  const { mean: avgRatio } = meanStd(ratioValues)

  // Neighbor averages per day for comparison
  const neighborAvgTotals = []
  if (hasNeighbors) {
    for (const day of mainDaily) {
      if (!day.hasData) continue
      let nSum = 0, nCount = 0
      for (const n of activeNeighbors) {
        const nDay = neighborDailyMap[n.serialNumber]?.find(d => d.dateStr === day.dateStr)
        if (nDay?.hasData) { nSum += nDay.total; nCount++ }
      }
      if (nCount > 0) neighborAvgTotals.push(nSum / nCount)
    }
  }

  // ── Build analysis segments ──────────────────────────────────────────────
  const segments = []

  // 1. Overall performance vs neighbors
  if (hasNeighbors && ratioValues.length >= MIN_DAYS_FOR_ANALYSIS) {
    const avgPct = Math.round(avgRatio)
    if (avgPct < 50) {
      segments.push({
        text: `Over de afgelopen ${mainDaysWithData.length} dagen presteert deze installatie gemiddeld ${avgPct}% van het ${label}gemiddelde — significant ondermaats.`,
        type: 'critical',
      })
    } else if (avgPct < 75) {
      segments.push({
        text: `Over de afgelopen ${mainDaysWithData.length} dagen presteert deze installatie gemiddeld ${avgPct}% van het ${label}gemiddelde — structureel onder verwachting.`,
        type: 'warning',
      })
    } else if (avgPct < 90) {
      segments.push({
        text: `Over de afgelopen ${mainDaysWithData.length} dagen presteert deze installatie gemiddeld ${avgPct}% van het ${label}gemiddelde — licht ondergemiddeld.`,
        type: 'warning',
      })
    } else if (avgPct <= 110) {
      segments.push({
        text: `Over de afgelopen ${mainDaysWithData.length} dagen presteert deze installatie in lijn met ${activeNeighbors.length} ${label} (${avgPct}%).`,
        type: 'good',
      })
    } else {
      segments.push({
        text: `Over de afgelopen ${mainDaysWithData.length} dagen presteert deze installatie ${avgPct}% van het ${label}gemiddelde — bovengemiddeld.`,
        type: 'good',
      })
    }
  } else if (!hasNeighbors) {
    segments.push({
      text: `Analyse over ${mainDaysWithData.length} dagen (geen ${label} beschikbaar voor vergelijking).`,
      type: 'normal',
    })
  }

  // 2. (removed: trend detection — not meaningful for PV over 10 days, variation is weather)
  {
  }

  // 3. Outlier days (>1.5σ from mean ratio, or from own mean if no neighbors)
  const outlierDays = []
  if (hasNeighbors && ratioValues.length >= MIN_DAYS_FOR_ANALYSIS) {
    const { mean: ratioMean, std: ratioStd } = meanStd(ratioValues)
    if (ratioStd > 0) {
      for (const dr of dailyRatios) {
        if (dr.ratio === null) continue
        const zScore = (dr.ratio - ratioMean) / ratioStd
        if (zScore < -1.5) {
          outlierDays.push({ ...dr, zScore, direction: 'low' })
        } else if (zScore > 2) {
          outlierDays.push({ ...dr, zScore, direction: 'high' })
        }
      }
    }
  } else {
    // No neighbors: detect outliers in own production
    if (stdMainTotal > 0) {
      for (const day of mainDaysWithData) {
        const z = (day.total - avgMainTotal) / stdMainTotal
        if (z < -1.5) {
          outlierDays.push({ dateStr: day.dateStr, ratio: null, mainTotal: day.total, zScore: z, direction: 'low' })
        }
      }
    }
  }

  if (outlierDays.length > 0) {
    const lowOutliers = outlierDays.filter(o => o.direction === 'low')
      .sort((a, b) => a.zScore - b.zScore)

    if (lowOutliers.length === 1) {
      const o = lowOutliers[0]
      const pctText = o.ratio !== null ? ` (${Math.round(o.ratio)}% van ${label})` : ''
      segments.push({
        text: `Op ${formatDateNL(o.dateStr)} was de productie opvallend laag${pctText}.`,
        date: o.dateStr,
        type: 'warning',
      })
    } else if (lowOutliers.length > 1) {
      segments.push({
        text: `${lowOutliers.length} dagen met opvallend lage productie.`,
        type: 'warning',
        outlierDays: lowOutliers.map(o => ({
          dateStr: o.dateStr,
          label: formatDateNL(o.dateStr),
          ratio: o.ratio !== null ? Math.round(o.ratio) : null,
        })),
      })
    }
  }

  // 4. Missing data days (main has no data, neighbors do)
  if (hasNeighbors) {
    const missingDays = []
    for (const day of mainDaily) {
      if (day.hasData) continue
      // Check if neighbors have data for this day
      let neighborsHaveData = false
      for (const n of activeNeighbors) {
        const nDay = neighborDailyMap[n.serialNumber]?.find(d => d.dateStr === day.dateStr)
        if (nDay?.hasData) { neighborsHaveData = true; break }
      }
      if (neighborsHaveData) missingDays.push(day.dateStr)
    }

    if (missingDays.length > 0) {
      if (missingDays.length === 1) {
        segments.push({
          text: `Geen data op ${formatDateNL(missingDays[0])} terwijl ${label} wel produceerden — mogelijke communicatiestoring.`,
          date: missingDays[0],
          type: 'critical',
        })
      } else {
        segments.push({
          text: `${missingDays.length} dagen zonder data terwijl ${label} wel produceerden — communicatieprobleem.`,
          type: 'critical',
        })
      }
    }
  }

  // 5. Shadow pattern detection (per-slot ratio analysis across days)
  let shadowInfo = null
  if (hasNeighbors && mainDaysWithData.length >= 3) {
    // Group quarter-hour data by date
    const mainByDate = {}
    mainData.forEach(item => {
      const d = item.date?.substring(0, 10)
      if (d) (mainByDate[d] = mainByDate[d] || []).push(item)
    })

    const neighborByDate = {}
    for (const n of activeNeighbors) {
      const nData = neighborDataMap?.[n.serialNumber]
      if (!nData) continue
      nData.forEach(item => {
        const d = item.date?.substring(0, 10)
        if (d) {
          if (!neighborByDate[d]) neighborByDate[d] = {}
          if (!neighborByDate[d][n.serialNumber]) neighborByDate[d][n.serialNumber] = []
          neighborByDate[d][n.serialNumber].push(item)
        }
      })
    }

    // Per-day: compute per-slot ratio and detect shadow onset
    const shadowOnsets = [] // { dateStr, onsetTime, preRatio, dropRatio, endTime }

    for (const day of mainDaysWithData) {
      const dayMainData = mainByDate[day.dateStr]
      if (!dayMainData || dayMainData.length < 20) continue

      // Build per-slot ratios for this day
      const mainByTime = {}
      dayMainData.forEach(item => {
        const time = item.date?.substring(11, 16) || ''
        const val = Math.max(0, item.delta_power ?? 0)
        mainByTime[time] = mainKwp ? val / mainKwp : val
      })

      const slotRatios = []
      const times = Object.keys(mainByTime).filter(t => {
        const h = parseInt(t)
        return h >= 7 && h <= 19
      }).sort()

      times.forEach(t => {
        let nSum = 0, nCount = 0
        for (const n of activeNeighbors) {
          const nDayData = neighborByDate[day.dateStr]?.[n.serialNumber]
          if (!nDayData) continue
          const nWp = neighborWpMap?.[n.serialNumber]
          const nKwp = (nWp ? nWp * 0.001 : null) || mainKwp
          const nItem = nDayData.find(item => item.date?.substring(11, 16) === t)
          if (nItem) {
            const nVal = Math.max(0, nItem.delta_power ?? 0)
            const nNorm = nKwp ? nVal / nKwp : nVal
            if (nNorm > 0) { nSum += nNorm; nCount++ }
          }
        }
        if (nCount > 0) {
          const nAvg = nSum / nCount
          if (nAvg > 10) slotRatios.push({ time: t, ratio: mainByTime[t] / nAvg })
        }
      })

      // Detect shadow onset: ratio drops from >0.65 to <0.45
      for (let i = 2; i < slotRatios.length; i++) {
        const prevAvg = (slotRatios[i - 2].ratio + slotRatios[i - 1].ratio) / 2
        const curr = slotRatios[i].ratio
        if (prevAvg > 0.65 && curr < 0.45) {
          let dropSum = curr, dropCount = 1, endTime = slotRatios[i].time
          for (let j = i + 1; j < slotRatios.length; j++) {
            if (slotRatios[j].ratio < 0.6) { dropSum += slotRatios[j].ratio; dropCount++; endTime = slotRatios[j].time }
            else break
          }
          shadowOnsets.push({
            dateStr: day.dateStr,
            onsetTime: slotRatios[i].time,
            preRatio: Math.round(prevAvg * 100),
            dropRatio: Math.round((dropSum / dropCount) * 100),
            endTime,
          })
          break
        }
      }
    }

    // Analyze shadow pattern across days
    if (shadowOnsets.length >= 1) {
      const onsetTimes = shadowOnsets.map(s => {
        const [h, m] = s.onsetTime.split(':').map(Number)
        return h * 60 + m
      })
      const avgOnsetMinutes = onsetTimes.reduce((a, b) => a + b, 0) / onsetTimes.length
      const avgOnsetH = Math.floor(avgOnsetMinutes / 60)
      const avgOnsetM = Math.round(avgOnsetMinutes % 60)
      const avgOnsetStr = `${String(avgOnsetH).padStart(2, '0')}:${String(avgOnsetM).padStart(2, '0')}`
      const avgDropRatio = Math.round(shadowOnsets.reduce((s, o) => s + o.dropRatio, 0) / shadowOnsets.length)
      const frequency = Math.round((shadowOnsets.length / mainDaysWithData.length) * 100)

      shadowInfo = {
        count: shadowOnsets.length,
        totalDays: mainDaysWithData.length,
        avgOnsetTime: avgOnsetStr,
        avgDropRatio,
        frequency,
        days: shadowOnsets,
      }

      if (shadowOnsets.length >= 3 || frequency >= 50) {
        segments.push({
          text: `Schaduwval gedetecteerd op ${shadowOnsets.length} van ${mainDaysWithData.length} dagen. Gemiddeld vanaf ${avgOnsetStr}, productie daalt naar ${avgDropRatio}% van ${label}. Dit wijst op een structureel obstakel.`,
          type: 'warning',
        })
      } else if (shadowOnsets.length >= 1) {
        const example = shadowOnsets[0]
        segments.push({
          text: `Schaduwval waargenomen op ${formatDateNL(example.dateStr)} om ${example.onsetTime}: productie daalt van ${example.preRatio}% naar ${example.dropRatio}% van ${label}.`,
          date: example.dateStr,
          type: 'warning',
        })
      }
    } else {
      // Fallback: morning vs afternoon block comparison
      let morningRatios = [], afternoonRatios = []
      for (const day of mainDaysWithData) {
        if (day.morning === 0 && day.afternoon === 0) continue
        let nMorning = 0, nAfternoon = 0, nCount = 0
        for (const n of activeNeighbors) {
          const nDay = neighborDailyMap[n.serialNumber]?.find(d => d.dateStr === day.dateStr)
          if (nDay?.hasData) { nMorning += nDay.morning; nAfternoon += nDay.afternoon; nCount++ }
        }
        if (nCount > 0) {
          const avgNM = nMorning / nCount
          const avgNA = nAfternoon / nCount
          if (avgNM > 0) morningRatios.push(day.morning / avgNM)
          if (avgNA > 0) afternoonRatios.push(day.afternoon / avgNA)
        }
      }
      if (morningRatios.length >= 3 && afternoonRatios.length >= 3) {
        const avgM = meanStd(morningRatios).mean
        const avgA = meanStd(afternoonRatios).mean
        if (avgM < 0.7 && avgA > 0.85) {
          segments.push({ text: `Structureel lagere ochtendproductie (${Math.round(avgM * 100)}% van ${label}) — mogelijke ochtendschaduw.`, type: 'warning' })
        } else if (avgA < 0.7 && avgM > 0.85) {
          segments.push({ text: `Structureel lagere middagproductie (${Math.round(avgA * 100)}% van ${label}) — mogelijke middagschaduw.`, type: 'warning' })
        } else if (avgM < 0.7 && avgA < 0.7) {
          segments.push({ text: `Ochtend- en middagproductie structureel lager dan ${label} — paneel/omvormer probleem.`, type: 'critical' })
        }
      }
    }
  }

  // 6. Consistency analysis
  if (hasNeighbors && ratioValues.length >= 5) {
    const { std: ratioStd } = meanStd(ratioValues)
    const cv = avgRatio > 0 ? (ratioStd / avgRatio) * 100 : 0 // coefficient of variation

    if (cv > 30) {
      segments.push({
        text: `Zeer wisselende prestatie t.o.v. ${label} (variatie ${Math.round(cv)}%) — incidentele storingen of wisselende schaduw.`,
        type: 'warning',
      })
    } else if (cv > 15) {
      segments.push({
        text: `Wisselende prestatie t.o.v. ${label} (variatie ${Math.round(cv)}%).`,
        type: 'normal',
      })
    } else if (cv < 8 && avgRatio >= 85) {
      segments.push({
        text: `Consistente prestatie over de hele periode.`,
        type: 'good',
      })
    }
  }

  // 7. Best and worst day
  if (mainDaysWithData.length >= 5) {
    const sorted = [...mainDaysWithData].filter(d => d.total > 0).sort((a, b) => b.total - a.total)
    if (sorted.length >= 2) {
      const best = sorted[0]
      const worst = sorted[sorted.length - 1]

      // Only mention if there's meaningful difference
      if (best.total > 0 && worst.total > 0) {
        const ratio = best.total / worst.total
        if (ratio > 3) {
          segments.push({
            text: `Beste dag: ${formatDateNL(best.dateStr)} (${Math.round(best.total)} Wh/kWp).`,
            date: best.dateStr,
            type: 'normal',
          })
          segments.push({
            text: `Slechtste dag: ${formatDateNL(worst.dateStr)} (${Math.round(worst.total)} Wh/kWp).`,
            date: worst.dateStr,
            type: 'normal',
          })
        }
      }
    }
  }

  // 8. Prognosis comparison
  let prognosisRatio = null
  if (expectedProduction && mainDaysWithData.length >= 3) {
    // Determine which month(s) the data spans
    const monthsInData = [...new Set(mainDaysWithData.map(d => parseInt(d.dateStr.substring(5, 7)) - 1))]
    // Calculate expected daily production for the period
    let expectedDailyTotal = 0
    for (const month of monthsInData) {
      const fraction = MONTHLY_SOLAR_FRACTIONS[month] ?? (1 / 12)
      const monthlyExpected = expectedProduction * fraction
      const daysInMonth = new Date(parseInt(dateList[0]), month + 1, 0).getDate()
      expectedDailyTotal += monthlyExpected / daysInMonth
    }
    expectedDailyTotal /= monthsInData.length // average if spanning months

    const actualAvgDaily = avgMainTotal * (mainKwp || 1) // convert back from Wh/kWp to Wh
    prognosisRatio = expectedDailyTotal > 0 ? Math.round((actualAvgDaily / expectedDailyTotal) * 100) : null

    if (prognosisRatio !== null) {
      if (prognosisRatio < 50) {
        segments.push({
          text: `Productie is ${prognosisRatio}% van de maandprognose — ver onder verwachting.`,
          type: 'critical',
        })
      } else if (prognosisRatio < 75) {
        segments.push({
          text: `Productie is ${prognosisRatio}% van de maandprognose — onder verwachting.`,
          type: 'warning',
        })
      } else if (prognosisRatio < 90) {
        segments.push({
          text: `Productie is ${prognosisRatio}% van de maandprognose — licht onder verwachting.`,
          type: 'warning',
        })
      } else if (prognosisRatio <= 115) {
        segments.push({
          text: `Productie is in lijn met de maandprognose (${prognosisRatio}%).`,
          type: 'good',
        })
      } else {
        segments.push({
          text: `Productie is ${prognosisRatio}% van de maandprognose — boven verwachting.`,
          type: 'good',
        })
      }
    }
  }

  // 9. Orientation & tilt estimation from clearest day
  const orientationEstimate = estimateOrientationAndTilt(mainData, dateList, mainWp)

  if (orientationEstimate) {
    const { azimuth, tilt, clearDay, smoothness, azimuthLabel } = orientationEstimate
    let text = `Geschatte oriëntatie: ~${azimuth}° (${azimuthLabel})`
    if (tilt !== null) text += `, helling: ~${tilt}°`
    text += ` (gebaseerd op productieprofiel van ${formatDateNL(clearDay)}, helderheid ${smoothness}%).`
    segments.push({ text, date: clearDay, type: 'normal' })
  }

  // ── Build summary ────────────────────────────────────────────────────────
  const summary = {
    daysAnalyzed: mainDaysWithData.length,
    avgDailyProduction: Math.round(avgMainTotal),
    avgRatioToNeighbors: hasNeighbors ? Math.round(avgRatio) : null,
    neighborsUsed: activeNeighbors.length,
    outlierCount: outlierDays.filter(o => o.direction === 'low').length,
    prognosisRatio,
    shadowInfo,
  }

  return {
    segments: segments.filter(s => s.type !== 'link'),
    dayLinks: segments.filter(s => s.date).map(s => ({ date: s.date, label: formatDateNL(s.date) })),
    dailyStats: mainDaily,
    dailyRatios,
    orientationEstimate,
    summary,
  }
}
